/**
 * The Workshop thread's composer, in the M7 rail's language (stage 2a): one
 * floating glass surface (the rail's own classes), the files waiting to go out
 * on top, then the field, then attach, the model and effort, and a square send
 * that takes the accent only when there is something to send.
 *
 * While a reply is written people keep writing, so Stop sits beside Send, and
 * Stop really stops it (useStopChatTurn asks the server to end the turn). In a
 * private thread Send queues the message until the reply ends; in a shared one
 * it joins the reply.
 * Voice is optional (`voice`, the rail's talk-then-check): Home and the
 * Workshop's own composers turn it on.
 *
 * Picked skills and agents sit on top as pills, and "/" at the start of the
 * field lists the Creos. Several can be picked: each "/" pick adds a pill,
 * and picking one already in takes it out.
 */
import { useLayoutEffect, useRef, useState, type FormEvent, type RefObject } from "react";
import { ArrowUp, Loader2, Mic, Square } from "lucide-react";
import type { ComposerVoice } from "../../agent/rail/RailComposer";
import { VoiceRecordingBar } from "../../agent/rail/RailVoice";
import type { ChatModel, ReasoningEffort } from "@/types/chat.type";
import type { ChatAttachmentItem } from "@/app/routes/dashboard/api/useSessionAttachments";
import { ComposerFiles } from "../../agent/rail/RailMedia";
import WorkshopAttach from "./WorkshopAttach";
import WorkshopModelMenu from "./WorkshopModelMenu";
import type { WorkshopQuickCreateItem, WorkshopQuickCreateRef } from "@/types/workshop.type";
import { canAddPick, matchQuickCreate, pickId } from "../quickCreate";
import { QuickCreatePills, QuickCreateSlashMenu } from "./QuickCreateParts";

/** The field grows to this many lines (fewer on a phone), or 40% of the thread. */
const CAP_LINES = 8;
const CAP_LINES_PHONE = 6;
const CAP_SHARE = 0.4;

type Props = {
  value: string;
  onChange: (value: string) => void;
  /** `from`: where the words sat in the field, for the bubble's flight into the thread. */
  onSubmit: (from: { left: number; top: number } | null) => void;
  placeholder: string;
  inputLabel: string;
  /** The field is shut: the thread is loading, a card waits, or a private reply streams. */
  disabled: boolean;
  isStreaming: boolean;
  onStop: () => void;
  isStopping: boolean;
  /** Sending while the assistant works: a shared thread joins its reply, a private one queues. */
  sendWhileStreaming: boolean;
  /** Send puts the message in the queue behind the reply being written. */
  queueing: boolean;
  /** A shared thread always uses the default model (the API refuses a pick). */
  showModel: boolean;
  models: ChatModel[];
  selectedModel: ChatModel | null;
  onModelChange: (model: ChatModel) => void;
  effort: ReasoningEffort;
  onEffortChange: (effort: ReasoningEffort) => void;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  onAttachFiles?: (files: File[]) => void;
  attachDisabled: boolean;
  attachAccept: string;
  photoAccept: string;
  fileAccept: string;
  /** Files attached since the last message of yours: they go out with the next one. */
  staged: ChatAttachmentItem[];
  onRetryAttachment?: (documentId: string) => void;
  /** A message held until an attached file finishes reading. */
  queued: boolean;
  isPhone: boolean;
  /** The skills and agents this message starts (its pills); empty = a plain message. */
  quickCreates?: WorkshopQuickCreateRef[];
  /** Picks that went while they sat here: their pills grey and Send waits for them to go. */
  unavailableQuickCreates?: WorkshopQuickCreateRef[];
  onRemoveQuickCreate?: (item: WorkshopQuickCreateRef) => void;
  /** What "/" lists; without it "/" is just a character. */
  quickCreateItems?: WorkshopQuickCreateItem[];
  onPickQuickCreate?: (item: WorkshopQuickCreateItem) => void;
  /** Talk, then check the words (the rail's recorder). Absent: no mic. */
  voice?: ComposerVoice;
};

const NO_PICKS: WorkshopQuickCreateRef[] = [];

/** "/" then a few words, on one line, opens the list. */
const SLASH = /^\/([^\n]{0,48})$/;

export default function WorkshopComposer({
  value,
  onChange,
  onSubmit,
  placeholder,
  inputLabel,
  disabled,
  isStreaming,
  onStop,
  isStopping,
  sendWhileStreaming,
  queueing,
  showModel,
  models,
  selectedModel,
  onModelChange,
  effort,
  onEffortChange,
  textareaRef,
  onAttachFiles,
  attachDisabled,
  attachAccept,
  photoAccept,
  fileAccept,
  staged,
  onRetryAttachment,
  queued,
  isPhone,
  quickCreates = NO_PICKS,
  unavailableQuickCreates = NO_PICKS,
  onRemoveQuickCreate,
  quickCreateItems,
  onPickQuickCreate,
  voice,
}: Props) {
  const voicePhase = voice?.phase ?? "idle";
  const formRef = useRef<HTMLFormElement>(null);
  const stopInPlace = isStreaming && !sendWhileStreaming;
  const blocked = unavailableQuickCreates.length > 0;
  const hasWords = value.trim() !== "" || quickCreates.length > 0;
  const ready =
    hasWords && !blocked && !disabled && (!isStreaming || sendWhileStreaming) && voice?.phase !== "writing";

  // "/" lists Quick Create until a pick, Escape, or the words stop looking like one.
  const slashQuery = quickCreateItems && onPickQuickCreate ? (SLASH.exec(value)?.[1] ?? null) : null;
  const [dismissedFor, setDismissedFor] = useState<string | null>(null);
  const slashOpen = slashQuery !== null && dismissedFor !== value && !disabled;
  const picked = new Set(quickCreates.map(pickId));
  // Picked cards stay listed (a second pick takes them out); the rest while there is room.
  const matches = slashOpen
    ? matchQuickCreate(
        (quickCreateItems ?? []).filter((item) => picked.has(pickId(item)) || canAddPick(quickCreates, item)),
        slashQuery,
        true,
      ).slice(0, 8)
    : [];
  const [active, setActive] = useState(0);
  const [activeFor, setActiveFor] = useState(slashQuery);
  if (activeFor !== slashQuery) {
    setActiveFor(slashQuery);
    setActive(0);
  }
  const pickSlash = (item: WorkshopQuickCreateItem) => {
    const inMessage = quickCreates.find((p) => pickId(p) === pickId(item));
    if (inMessage) onRemoveQuickCreate?.(inMessage);
    else onPickQuickCreate?.(item);
    onChange("");
  };
  const shownPlaceholder = stopInPlace ? "Working. Stop to interrupt." : placeholder;

  /* The field grows with the words up to its cap, then scrolls with its first
     lines fading. Written to the DOM: it changes on every keystroke. */
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    const grow = () => {
      el.style.height = "auto";
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 21;
      const thread = el.closest(".ws-thread")?.clientHeight ?? Infinity;
      const cap = Math.max(
        lineHeight * 2,
        Math.min(lineHeight * (isPhone ? CAP_LINES_PHONE : CAP_LINES), thread * CAP_SHARE),
      );
      const needed = el.scrollHeight;
      el.style.height = `${Math.min(needed, cap)}px`;
      el.style.overflowY = needed > cap + 1 ? "auto" : "hidden";
    };
    grow();
    const observer = new ResizeObserver(grow);
    observer.observe(el.closest(".ws-thread") ?? el);
    return () => observer.disconnect();
  }, [value, shownPlaceholder, textareaRef, isPhone]);

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (!ready) return;
    const box = textareaRef.current?.getBoundingClientRect();
    onSubmit(box ? { left: box.left, top: box.top } : null);
  };

  const pending = staged.filter((a) => a.state === "uploading" || a.state === "processing");
  const stopButton = (
    <button
      type="button"
      className="m7r-send ws-stop"
      data-stop
      disabled={isStopping}
      aria-label={isStopping ? "Stopping the reply" : "Stop the reply"}
      title="Stop the reply"
      onClick={onStop}
    >
      {isStopping ? (
        <Loader2 className="animate-spin" aria-hidden />
      ) : (
        <Square aria-hidden fill="currentColor" strokeWidth={0} />
      )}
    </button>
  );

  return (
    <form
      ref={formRef}
      className="m7r-composer co-chrome-sheet ws-composer"
      data-idle={disabled || stopInPlace || undefined}
      data-recording={voicePhase === "recording" || undefined}
      onSubmit={submit}
    >
      {voicePhase === "recording" && voice ? (
        <VoiceRecordingBar levels={voice.levels} elapsedMs={voice.elapsedMs} onCancel={voice.onCancel} onStop={voice.onStop} />
      ) : (
      <>
      {slashOpen ? (
        <QuickCreateSlashMenu
          matches={matches}
          picked={picked}
          active={active}
          onHover={setActive}
          onPick={pickSlash}
        />
      ) : null}
      <ComposerFiles items={staged} onRetry={onRetryAttachment} />
      {quickCreates.length > 0 ? (
        <div className="ws-qc-field-pill">
          <QuickCreatePills items={quickCreates} onRemove={onRemoveQuickCreate} unavailable={unavailableQuickCreates} />
        </div>
      ) : null}
      {queued ? (
        <p className="m7r-queued" role="status">
          <Loader2 className="animate-spin" aria-hidden />
          Sends when {pending.length === 1 ? "your file is" : "your files are"} ready
        </p>
      ) : null}

      <label htmlFor="ws-thread-field" className="co-sr-only">
        {inputLabel}
      </label>
      <div className="m7r-field-wrap">
        <textarea
          id="ws-thread-field"
          ref={textareaRef}
          rows={1}
          className="m7r-field co-input-own-focus"
          value={value}
          maxLength={10_000}
          placeholder={shownPlaceholder}
          disabled={disabled}
          role={slashOpen && matches.length > 0 ? "combobox" : undefined}
          aria-expanded={slashOpen && matches.length > 0 ? true : undefined}
          aria-controls={slashOpen && matches.length > 0 ? "ws-qc-slash" : undefined}
          aria-activedescendant={slashOpen && matches.length > 0 ? `ws-qc-opt-${active}` : undefined}
          onChange={(e) => onChange(e.target.value)}
          onScroll={(e) =>
            e.currentTarget.toggleAttribute("data-scrolled", e.currentTarget.scrollTop > 2)
          }
          onKeyDown={(e) => {
            if (slashOpen && matches.length > 0 && !e.nativeEvent.isComposing) {
              if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                e.preventDefault();
                const step = e.key === "ArrowDown" ? 1 : -1;
                setActive((i) => (i + step + matches.length) % matches.length);
                return;
              }
              if ((e.key === "Enter" && !e.shiftKey) || e.key === "Tab") {
                e.preventDefault();
                pickSlash(matches[Math.min(active, matches.length - 1)]);
                return;
              }
            }
            if (slashOpen && e.key === "Escape") {
              e.preventDefault();
              setDismissedFor(value);
              return;
            }
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              submit();
            }
          }}
        />
      </div>

      {voicePhase === "writing" || voicePhase === "checking" ? (
        <div className="m7r-voice-words" aria-live="polite">
          {voicePhase === "writing" && !value.trim() ? <span className="m7r-voice-skeleton" aria-hidden /> : null}
          <span className="m7r-voice-hint">{voicePhase === "writing" ? "Writing it down" : "Check the words, then send"}</span>
        </div>
      ) : null}

      <div className="m7r-controls ws-controls">
        {onAttachFiles ? (
          <WorkshopAttach
            onAttachFiles={onAttachFiles}
            disabled={attachDisabled}
            isPhone={isPhone}
            accept={attachAccept}
            photoAccept={photoAccept}
            fileAccept={fileAccept}
          />
        ) : null}
        {showModel ? (
          <WorkshopModelMenu
            models={models}
            selectedModel={selectedModel}
            onModelChange={onModelChange}
            effort={effort}
            onEffortChange={onEffortChange}
            disabled={disabled}
            isPhone={isPhone}
          />
        ) : null}
        <span className="ws-controls-gap" aria-hidden="true" />
        {voice && !isStreaming ? (
          <button
            type="button"
            className="m7r-mic"
            aria-label="Talk, then check the words"
            title="Talk, then check the words"
            disabled={disabled || voicePhase === "writing"}
            onClick={voice.onStart}
          >
            <Mic aria-hidden strokeWidth={1.75} />
          </button>
        ) : null}
        {isStreaming && sendWhileStreaming ? stopButton : null}
        {stopInPlace ? (
          stopButton
        ) : (
          <button
            type="submit"
            className="m7r-send"
            data-ready={ready || undefined}
            aria-disabled={!ready}
            aria-label={queueing ? "Queue message" : "Send"}
            title={queueing ? "Queue message" : "Send"}
          >
            <ArrowUp aria-hidden strokeWidth={2} />
          </button>
        )}
      </div>
      </>
      )}
    </form>
  );
}
